/**
 * Pause / continue handlers — plus the pause-timeout state machine.
 *
 * Pause stops the MediaRecorder, the periodic chunk upload + IDB save
 * timers, and the elapsed counter, then sends a `{ type: "pause" }` frame
 * over the streaming WS so the backend can squelch its ASR pipe. It also
 * hits `POST /api/companies/:cid/meetings/:id/pause` so the server records
 * `paused_at` for the orphan-recovery loop.
 *
 * After 5 minutes paused, a confirmation modal pops asking the user to
 * confirm they're still around. Another 5 minutes without a response and
 * the recording auto-finalizes via `onPauseTimeoutRef`. Confirming "Keep
 * paused" refreshes both the client timer (full 10-min window again) and
 * `paused_at` on the server (idempotent re-pause).
 *
 * Continue resumes the recorder, restarts all the timers, hits the resume
 * endpoint to clear `paused_at`, and re-opens the WS in streaming mode.
 */
import type { TStreamControlMessage } from "@lexicanon/shared"
import { useCallback, useEffect, useRef, useState } from "react"
import type { CompanyApi } from "../../../hooks/useCompanyApi"
import { pauseMeeting, resumeMeeting } from "../../../lib/api"
import type { TRecordingMode, TRecordingState } from "../interfaces"

const PAUSE_CONFIRM_DELAY_MS = 5 * 60_000
const PAUSE_CULL_AFTER_CONFIRM_MS = 5 * 60_000
const COUNTDOWN_TICK_MS = 1_000

interface IProps {
  meetingId: string
  mode: TRecordingMode
  company: CompanyApi | null
  elapsed: number
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>
  wsRef: React.MutableRefObject<WebSocket | null>
  /** Cached first MediaRecorder chunk (WebM/Opus init header). Re-sent on the
   *  fresh WebSocket opened by resume so the backend accepts the Opus stream. */
  firstHeaderChunkRef: React.MutableRefObject<Blob | null>
  startTimeRef: React.MutableRefObject<number>
  pausedElapsedRef: React.MutableRefObject<number>
  heartbeatIntervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>
  startElapsedTimer: () => void
  stopElapsedTimer: () => void
  startPeriodicSave: () => void
  stopPeriodicSave: () => void
  startChunkUploadTimer: () => void
  stopChunkUploadTimer: () => void
  openStreamingWs: () => WebSocket | null
  setState: (state: TRecordingState) => void
  /**
   * Mutable ref the caller wires up after `useFinishLifecycle` runs, since
   * `handleFinish` is created later in the parent. The hook reads `.current`
   * lazily so the circular dependency stays resolved.
   */
  onPauseTimeoutRef: React.MutableRefObject<(() => void) | null>
}

interface IUsePauseResumeResult {
  handlePause: () => void
  handleContinue: () => void
  /** Whether the "Still recording?" modal is currently visible. */
  pauseConfirmOpen: boolean
  /** Seconds remaining on the auto-finalize countdown once the modal is open. */
  pauseRemainingSeconds: number
  /** User clicked "Keep paused" — refreshes both client timer and server paused_at. */
  handleConfirmKeepPaused: () => void
  /** User clicked "Finalize now" — triggers the finalize callback. */
  handleConfirmFinalize: () => void
}

export function usePauseResume(props: IProps): IUsePauseResumeResult {
  const {
    meetingId,
    mode,
    company,
    elapsed,
    mediaRecorderRef,
    wsRef,
    firstHeaderChunkRef,
    startTimeRef,
    pausedElapsedRef,
    startElapsedTimer,
    stopElapsedTimer,
    startPeriodicSave,
    stopPeriodicSave,
    startChunkUploadTimer,
    stopChunkUploadTimer,
    openStreamingWs,
    setState,
    onPauseTimeoutRef,
  } = props

  const [pauseConfirmOpen, setPauseConfirmOpen] = useState<boolean>(false)
  const [pauseRemainingSeconds, setPauseRemainingSeconds] = useState<number>(0)

  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cullTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearPauseTimers = useCallback((): void => {
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current)
      confirmTimerRef.current = null
    }
    if (cullTimerRef.current) {
      clearTimeout(cullTimerRef.current)
      cullTimerRef.current = null
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [])

  const triggerFinalize = useCallback((): void => {
    clearPauseTimers()
    setPauseConfirmOpen(false)
    const cb = onPauseTimeoutRef.current
    if (cb) cb()
  }, [clearPauseTimers, onPauseTimeoutRef])

  const openConfirmModal = useCallback((): void => {
    setPauseConfirmOpen(true)
    setPauseRemainingSeconds(Math.floor(PAUSE_CULL_AFTER_CONFIRM_MS / 1000))

    cullTimerRef.current = setTimeout(() => {
      triggerFinalize()
    }, PAUSE_CULL_AFTER_CONFIRM_MS)

    const deadline = Date.now() + PAUSE_CULL_AFTER_CONFIRM_MS
    countdownTimerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      setPauseRemainingSeconds(remaining)
    }, COUNTDOWN_TICK_MS)
  }, [triggerFinalize])

  const startPauseTimers = useCallback((): void => {
    clearPauseTimers()
    confirmTimerRef.current = setTimeout(() => {
      openConfirmModal()
    }, PAUSE_CONFIRM_DELAY_MS)
  }, [clearPauseTimers, openConfirmModal])

  // biome-ignore lint/correctness/useExhaustiveDependencies: pausedElapsedRef is a stable ref
  const handlePause = useCallback((): void => {
    mediaRecorderRef.current?.pause()
    stopChunkUploadTimer()
    stopElapsedTimer()
    stopPeriodicSave()
    pausedElapsedRef.current = elapsed

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const pauseMsg: TStreamControlMessage = { type: "pause" }
      wsRef.current.send(JSON.stringify(pauseMsg))
    }

    if (company) {
      pauseMeeting(company, meetingId).catch(() => undefined)
    }

    startPauseTimers()
    setState("paused")
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pausedElapsedRef is a stable ref
  }, [
    elapsed,
    stopChunkUploadTimer,
    stopElapsedTimer,
    stopPeriodicSave,
    wsRef,
    company,
    meetingId,
    startPauseTimers,
    setState,
  ])

  // biome-ignore lint/correctness/useExhaustiveDependencies: startTimeRef and firstHeaderChunkRef are stable refs
  const handleContinue = useCallback((): void => {
    clearPauseTimers()
    setPauseConfirmOpen(false)

    if (company) {
      resumeMeeting(company, meetingId).catch(() => undefined)
    }

    mediaRecorderRef.current?.resume()
    startTimeRef.current = Date.now()
    setState("recording")
    startElapsedTimer()
    startChunkUploadTimer()
    startPeriodicSave()

    if (mode === "streaming") {
      const ws = openStreamingWs()
      // The reused MediaRecorder will NOT emit a fresh WebM/Opus header on
      // resume — it only emits mid-stream Opus frames. Re-send the cached
      // header on the new socket so the backend accepts the stream; otherwise
      // it rejects the headerless frames ("Invalid audio type") and live
      // transcription dies after the first pause.
      const headerChunk = firstHeaderChunkRef.current
      if (ws && headerChunk) {
        const sendHeader = (): void => {
          headerChunk.arrayBuffer().then((buf) => {
            if (ws.readyState === WebSocket.OPEN) ws.send(buf)
          })
        }
        if (ws.readyState === WebSocket.OPEN) {
          sendHeader()
        } else {
          ws.addEventListener("open", sendHeader, { once: true })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startTimeRef and firstHeaderChunkRef are stable refs
  }, [
    startElapsedTimer,
    startChunkUploadTimer,
    startPeriodicSave,
    mode,
    openStreamingWs,
    company,
    meetingId,
    clearPauseTimers,
    setState,
  ])

  const handleConfirmKeepPaused = useCallback((): void => {
    // Idempotent re-pause refreshes the server's pause window AND the client
    // timer chain — full 10-min window again, modal closes.
    if (company) {
      pauseMeeting(company, meetingId).catch(() => undefined)
    }
    setPauseConfirmOpen(false)
    startPauseTimers()
  }, [company, meetingId, startPauseTimers])

  const handleConfirmFinalize = useCallback((): void => {
    triggerFinalize()
  }, [triggerFinalize])

  // Tidy up on unmount.
  useEffect(() => {
    return () => clearPauseTimers()
  }, [clearPauseTimers])

  return {
    handlePause,
    handleContinue,
    pauseConfirmOpen,
    pauseRemainingSeconds,
    handleConfirmKeepPaused,
    handleConfirmFinalize,
  }
}
